import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import {
  saveTeamOrder,
  createStripeConfirmationQuote,
  finalizeStripePayment,
  createPaymentFeeQuote,
} from "actions/teamActions";
import { isEmpty, noop, filter, head, get } from "lodash/fp";
import { Checkbox, CircularProgress } from "@material-ui/core";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";
import { isNotEmpty } from "../../utils/funcUtils";
import { getSelectedTeam } from "../../reducers/teamSelectors";
import { format, utcToZonedTime } from "date-fns-tz";
import { asHKCurrency, formatCurrency } from "../helpers";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import StripeCheckoutForm from "./StripeCheckoutForm";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import Select from "../common/Select";
import { getMootRoundTimeOptions } from "../../utils/dateUtils";
import moot from "../settings/Moot";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_CLIENT_SECRET);
const STRIPE_REDIRECT_PARAMS = [
  "payment_intent",
  "payment_intent_client_secret",
  "redirect_status",
];
const STRIPE_REDIRECT_RESULT_KEY = "stripeRedirectResult";

const getStoredStripeRedirectResult = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(STRIPE_REDIRECT_RESULT_KEY));
  } catch (error) {
    return null;
  }
};

const storeStripeRedirectResult = (result) => {
  try {
    window.sessionStorage.setItem(
      STRIPE_REDIRECT_RESULT_KEY,
      JSON.stringify(result),
    );
  } catch (error) {}
};

const clearStripeRedirectParams = () => {
  const url = new URL(window.location.href);
  STRIPE_REDIRECT_PARAMS.forEach((param) => url.searchParams.delete(param));
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
};

const PaymentProcessing = ({
  existingTeamData = {},
  onPaymentTypeChanged = noop,
  onPaymentCompleted = noop,
  selectedTeam = {},
  saveTeamOrder = noop,
  getSelectedTeam = noop,
  paymentOverride = false,
  showExsitingPaymentNotification = true,
  paymentLabel = "",
  minAmount = 1500,
  maxAmount = 10000,
  balanceOwed = 0,
  paymentHelperText = "",
  transactionType,
  showBankTransferRegInstructions = true,
  resetPaymentMethodOnCompletion = false,
  paymentInProcess = false,
  onPaymentInProcess = noop,
  createStripeConfirmationQuote = noop,
  finalizeStripePayment = noop,
  createPaymentFeeQuote = noop,
}) => {
  const labelStyle = {
    fontWeight: "bold",
  };

  const lineStyle = {
    margin: ".5rem 0",
  };

  const buildPaymentAmounts = () => {
    const depositAmount = parseFloat(moot.teamDepositAmount ?? 1500);
    const smallTeamRegistrationFee = parseFloat(
      moot.inPersonRegistrationFeeSmallTeam ?? 9500,
    );
    const registrationFee = parseFloat(moot.inPersonRegistrationFee ?? 10000);

    const nonrefundableDeposit = {
      name: `${asHKCurrency(depositAmount)} (Non-refundable Deposit)`,
      id: depositAmount.toFixed(2),
    };

    const remainingBalance = {
        name: `${asHKCurrency(Math.abs(balanceOwed))} (Remaining Balance)`,
        id: Math.abs(balanceOwed).toFixed(2),
    };

    const smallTeamFee = {
        name: `${asHKCurrency(smallTeamRegistrationFee)} (Small Team Registration Fee)`,
        id: smallTeamRegistrationFee.toFixed(2),
    };

    const standardRegistrationFee = {
        name: `${asHKCurrency(registrationFee)} (Standard Registration Fee)`,
        id: registrationFee.toFixed(2),
    };


    let amountOptions = [nonrefundableDeposit, smallTeamFee, standardRegistrationFee];

    if (balanceOwed < 0 && balanceOwed !== (registrationFee * -1)) {
      amountOptions = [remainingBalance];
    }    

    return amountOptions;
  };

  const paymentAmounts = buildPaymentAmounts();
  const defaultPaymentAmount = paymentAmounts[0].id;

  const [isBusy, setBusy] = useState(false);
  const [paymentType, setPaymentType] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(defaultPaymentAmount);
  const [finalPaymentAmount, setFinalPaymentAmount] =
    useState(defaultPaymentAmount);
  const [paypalFeeQuote, setPaypalFeeQuote] = useState();
  const [payProcessingFees, setPayProcessingFees] = useState(true);
  const [paymentError, setPaymentError] = useState(false);
  const [paypalError, setPaypalError] = useState("");
  const storedStripeRedirectResult = useRef(getStoredStripeRedirectResult());
  const [stripeRedirectError, setStripeRedirectError] = useState(
    storedStripeRedirectResult.current?.error || "",
  );
  const [stripeRedirectStatus, setStripeRedirectStatus] = useState(
    storedStripeRedirectResult.current?.status || "",
  );
  const isMounted = useRef(true);
  const stripeRedirectHandled = useRef(false);

  const resetPaymentMethod = () => {
    if (resetPaymentMethodOnCompletion && isMounted.current) {
      setPaymentType(null);
      setPaypalFeeQuote(null);
      onPaymentTypeChanged(null);
    }
  };

  useEffect(() => () => {
    isMounted.current = false;
  }, []);

  useEffect(() => {
    if (!stripeRedirectError && !stripeRedirectStatus) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      window.sessionStorage.removeItem(STRIPE_REDIRECT_RESULT_KEY);
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [stripeRedirectError, stripeRedirectStatus]);

  const applyProcessingFeeChoice = (feeQuote) =>
    payProcessingFees
      ? {
          ...feeQuote,
          processingFeesPaid: true,
        }
      : {
          ...feeQuote,
          feeAmount: 0,
          grossAmount: finalPaymentAmount,
          feePolicy: "NO_FEE",
          rate: 0,
          fixedFee: 0,
          processingFeesPaid: false,
        };

  const getPaypalFeeQuote = async () => {
    const feeQuote = await createPaymentFeeQuote({
      processor: "paypal",
      paymentMethod: "paypal-card",
      country: "INTL",
      amount: finalPaymentAmount,
      teamId: selectedTeam.id,
      transactionType,
    });
    const adjustedFeeQuote = applyProcessingFeeChoice(feeQuote);

    setPaypalFeeQuote(adjustedFeeQuote);
    return adjustedFeeQuote;
  };

  useEffect(() => {
    const fetchPaypalFeeQuote = async () => {
      if (
        paymentType !== "paypal" ||
        paymentError ||
        !selectedTeam?.id ||
        !transactionType
      ) {
        setPaypalFeeQuote(null);
        return;
      }

      await getPaypalFeeQuote();
    };

    fetchPaypalFeeQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createPaymentFeeQuote,
    finalPaymentAmount,
    payProcessingFees,
    paymentError,
    paymentType,
    selectedTeam?.id,
    transactionType,
  ]);

  const validatePaymentAmount = (data, actions) => {
    if (paymentError) {
      return actions.reject();
    }

    if (
      (paymentAmount < minAmount || paymentAmount > maxAmount) &&
      paymentOverride === false
    ) {
      setPaymentError(
        `Please enter a payment amount between ${asHKCurrency(minAmount)} and ${asHKCurrency(maxAmount)}`,
      );
      return actions.reject();
    } else {
      setPaymentError(false);
      return actions.resolve();
    }
  };

  const createOrder = async (data, actions, paymentMethod) => {
    const paymentFeeQuote = paypalFeeQuote || (await getPaypalFeeQuote());
    const orderObject = {
      purchase_units: [
        {
          amount: {
            value: paymentFeeQuote?.grossAmount,
          },
          description: selectedTeam.school,
          custom_id: `${selectedTeam.id}|${transactionType}|${paymentFeeQuote?.baseAmount}|${paymentFeeQuote?.feeAmount}|${paymentFeeQuote?.feePolicy}|${paymentFeeQuote?.rate ?? ""}|${paymentFeeQuote?.fixedFee ?? ""}|${paymentFeeQuote?.country || ""}|${paymentMethod}`,
        },
      ],
      payment_source: {
        paypal: {
          experience_context: {
            shipping_preference: "NO_SHIPPING",
          },
        },
      },
    };
    return await actions.order.create(orderObject);
  };
  const resetPaypalPayment = (message) => {
    if (!isMounted.current) {
      return;
    }

    setBusy(false);
    setPaypalError(message);
    onPaymentInProcess(false);
  };
  const onPaypalClick = (data, actions) => {
    setPaypalError("");
    return validatePaymentAmount(data, actions);
  };
  const onPaypalCancel = () => {
    resetPaypalPayment("Payment was canceled. Please try again.");
  };
  const onPaypalError = () => {
    resetPaypalPayment(
      "PayPal could not process the payment. Please verify your details or try another payment method.",
    );
  };
  const onApprove = async (data, actions) => {
    setBusy(true);
    onPaymentInProcess();

    let order;
    try {
      order = await actions.order.get(data.orderID);
    } catch (error) {
      onPaypalError();
      throw error;
    }
    let details = order;
    if (order.status !== "APPROVED") {
      resetPaypalPayment("PayPal could not approve the payment. Please try again.");
    } else {
      try {
        details = await actions.order.capture();
      } catch (e) {
        onPaypalError();
        return order;
      }
      try {
        const amount = get("purchase_units[0].amount.value", details);
        const description = `PayPal: ${get("purchase_units[0].description", details)}`;
        const customIdParts = get(
          "purchase_units[0].custom_id",
          details,
        )?.split("|");
        const paymentFeeQuote =
          customIdParts?.length >= 9
            ? {
                processor: "paypal",
                baseAmount: customIdParts[2],
                feeAmount: customIdParts[3],
                grossAmount: amount,
                feePolicy: customIdParts[4],
                rate: customIdParts[5],
                fixedFee: customIdParts[6],
                country: customIdParts[7],
                paymentMethod: customIdParts[8],
                processingFeesPaid: parseFloat(customIdParts[3]) > 0,
              }
            : null;

        if (paymentFeeQuote) {
          details.paymentFeeQuote = paymentFeeQuote;
        }

        await saveTeamOrder(
          existingTeamData.id,
          details,
          description,
          amount,
          transactionType,
        );
        await onPaymentCompleted();
        await getSelectedTeam(true);
        resetPaymentMethod();
      } catch (error) {
        onPaymentInProcess(false);
      }

      if (isMounted.current) {
        setBusy(false);
      }
    }
    return order;
  };
  const onStripePaymentCompleted = async () => {
    try {
      await onPaymentCompleted();
      await getSelectedTeam(true);
      resetPaymentMethod();
    } catch (error) {}

    if (isMounted.current) {
      setBusy(false);
    }
  };
  const onStripePaymentStarted = () => {
    setBusy(true);
    onPaymentInProcess();
  };
  const onStripePaymentFailed = () => {
    if (isMounted.current) {
      setBusy(false);
    }
    onPaymentInProcess(false);
  };

  const onStripePaymentPending = async () => {
    if (transactionType !== "INITIAL") {
      await onPaymentCompleted();
    }
  };

  useEffect(() => {
    if (stripeRedirectHandled.current) {
      return;
    }

    const returnParams = new URLSearchParams(window.location.search);
    const clientSecret = returnParams.get("payment_intent_client_secret");
    const redirectStatus = returnParams.get("redirect_status");
    if (!clientSecret && !redirectStatus) {
      return;
    }

    stripeRedirectHandled.current = true;

    const handleStripeRedirect = async () => {
      setBusy(true);
      setStripeRedirectError("");
      setStripeRedirectStatus("");

      try {
        if (!clientSecret) {
          throw new Error("Unable to verify the Alipay payment. Please try again.");
        }

        const stripe = await stripePromise;
        if (!stripe) {
          throw new Error("Unable to load Stripe. Please refresh and try again.");
        }

        const result = await stripe.retrievePaymentIntent(clientSecret);
        if (result.error) {
          throw new Error(result.error.message);
        }

        if (!isMounted.current) {
          return;
        }

        const paymentIntentStatus = result.paymentIntent?.status;

        switch (paymentIntentStatus) {
          case "succeeded": {
            onPaymentInProcess();
            await onPaymentCompleted();
            const refreshedTeam = await getSelectedTeam(true);
            resetPaymentMethod();
            const hasConfirmedPayment = refreshedTeam?.Payments?.some(
              (payment) => payment.type !== "FEE",
            );
            if (!hasConfirmedPayment && isMounted.current) {
              const status =
                "Your Alipay payment succeeded and is being recorded. Please check the payment history again shortly.";
              storeStripeRedirectResult({ status });
              setStripeRedirectStatus(status);
              onPaymentInProcess(false);
            }
            break;
          }
          case "processing": {
            const status =
              "Your Alipay payment is still processing. Please check the payment history again shortly.";
            storeStripeRedirectResult({ status });
            setStripeRedirectStatus(status);
            break;
          }
          case "requires_payment_method":
          case "canceled": {
            const error =
              "The Alipay payment was declined or canceled. Please try again.";
            storeStripeRedirectResult({ error });
            setStripeRedirectError(error);
            onPaymentInProcess(false);
            break;
          }
          default: {
            const error =
              "The Alipay payment was not completed. Please try again.";
            storeStripeRedirectResult({ error });
            setStripeRedirectError(error);
            onPaymentInProcess(false);
            break;
          }
        }
        clearStripeRedirectParams();
      } catch (error) {
        if (isMounted.current) {
          storeStripeRedirectResult({ error: error.message });
          setStripeRedirectError(error.message);
        }
        onPaymentInProcess(false);
        clearStripeRedirectParams();
      } finally {
        if (isMounted.current) {
          setBusy(false);
        }
      }
    };

    handleStripeRedirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPaymentAmountEntered = async (e) => {
    const finalAmount = parseFloat(e.target.value).toFixed(2);
    setPaymentAmount(finalAmount);
    setFinalPaymentAmount(finalAmount);
    if (
      (finalAmount < minAmount || finalAmount > maxAmount) &&
      paymentOverride === false
    ) {
      setPaymentError(
        `Please enter a payment amount between ${asHKCurrency(minAmount)} and ${asHKCurrency(maxAmount)}`,
      );
    } else {
      setPaymentError(false);
    }
  };
  const initialPayment = head(
    filter((p) => p.type !== "FEE", selectedTeam?.Payments),
  );
  const showPaymentBusy = isBusy || paymentInProcess;

  return (
    <>
      {isNotEmpty(initialPayment) && showExsitingPaymentNotification && (
        <Typography>
          A payment in the amount of {asHKCurrency(initialPayment.amount)}{" "}
          received on{" "}
          {format(
            utcToZonedTime(initialPayment.createdAt, "Asia/Hong_Kong"),
            "eee. d MMMM h:mm aa",
          )}{" "}
          HKT
        </Typography>
      )}
      {(isEmpty(initialPayment) || !showExsitingPaymentNotification) && (
        <>
          {showPaymentBusy ? <CircularProgress size={35} /> : null}
          {stripeRedirectError && (
            <Typography color="error" style={{ marginTop: "1rem" }}>
              {stripeRedirectError}
            </Typography>
          )}
          {stripeRedirectStatus && (
            <Typography style={{ marginTop: "1rem" }}>
              {stripeRedirectStatus}
            </Typography>
          )}
          <div
            style={{
              display:
                showPaymentBusy || stripeRedirectStatus ? "none" : "block",
            }}
          >
            <>
                {paymentLabel !== "" && (
                <Typography
                    variant={transactionType === "INITIAL" ? "body1" : "h5"}
                    style={{ marginTop: "1rem" }}
                  >
                    {paymentLabel}
                  </Typography>
                )}
                  <Grid item xs={12} md={6} style={{ margin: "1rem 0 0 0" }}>
                    <Select
                      onChange={(e) => {
                        onPaymentAmountEntered(e);
                        setPaypalFeeQuote(null);
                      }}
                      label="Please select the payment amount"
                      value={paymentAmount}
                      options={paymentAmounts}
                      required
                      helperText={paymentHelperText}
                    />

                    {paymentError && (
                      <Typography color="error">{paymentError}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={12} style={{ marginBottom: "2rem" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={payProcessingFees}
                          disabled
                          onChange={() => {}}
                        />
                      }
                      label={
                        <span style={{ color: "rgba(0, 0, 0, 0.87)" }}>
                          I am happy to pay the processing fee so that the Team's payments are made in accordance with the VEM Rules
                        </span>
                      }
                    />
                  </Grid>
                  
              <FormControl component="fieldset">
                <FormLabel component="legend" required>
                  Please select your intended payment method. Please note, bank
                  transfers must be remitted separate from the Team Account
                  directly through the bank.
                </FormLabel>
                <RadioGroup
                  name="type"
                  value={paymentType}
                  onChange={(e) => {
                    setPaymentType(e.target.value);
                    onPaymentTypeChanged(e.target.value);
                  }}
                  row
                  style={{ marginTop: "0", columnGap: "1rem" }}
                >
                  <FormControlLabel
                    value="paypal"
                    control={<Radio color="primary" required />}
                    label="PayPal / Credit Card"
                  />
                  <FormControlLabel
                    value="stripe"
                    control={<Radio color="primary" required />}
                    label="Stripe / Credit Card / Link / Alipay"
                  />
                  <FormControlLabel
                    value="bankTransfer"
                    control={<Radio color="primary" required />}
                    label="Bank Transfer"
                  />
                </RadioGroup>
              </FormControl>
              {paymentType !== "bankTransfer" && (
                <>
                  

                  {paymentType === "stripe" && (
                    <Elements
                      stripe={stripePromise}
                      options={{
                        mode: "payment",
                        amount: Math.round(parseFloat(finalPaymentAmount) * 100),
                        currency: "hkd",
                        paymentMethodCreation: "manual",
                        paymentMethodTypes: ["card", "link", "alipay"],
                      }}
                    >
                      <StripeCheckoutForm
                        amount={finalPaymentAmount}
                        teamId={selectedTeam.id}
                        teamName={selectedTeam.school}
                        transactionType={transactionType}
                        createStripeConfirmationQuote={
                          createStripeConfirmationQuote
                        }
                        finalizeStripePayment={finalizeStripePayment}
                        onStripePaymentStarted={onStripePaymentStarted}
                        onStripePaymentCompleted={onStripePaymentCompleted}
                        onStripePaymentFailed={onStripePaymentFailed}
                        onStripePaymentPending={onStripePaymentPending}
                        payProcessingFees={payProcessingFees}
                      />
                    </Elements>
                  )}

                  {paymentType === "paypal" && (
                    <PayPalScriptProvider
                      options={{
                        "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
                        components: "buttons",
                        currency: "HKD",
                      }}
                    >
                      {paypalFeeQuote && (
                        <Typography style={{ marginBottom: "1rem" }}>
                          <span style={{fontSize: '1.25rem', fontWeight: 'bold'}}>
                            PayPal total:{" "}
                            {asHKCurrency(paypalFeeQuote.grossAmount)}
                          </span>{" "}
                          {payProcessingFees
                            ? `(includes estimated ${asHKCurrency(paypalFeeQuote.feeAmount)} processing fee)`
                            : "(excludes processing fee)"}
                        </Typography>
                      )}
                      {paypalError && (
                        <Typography color="error" style={{ marginBottom: "1rem" }}>
                          {paypalError}
                        </Typography>
                      )}
                      {payProcessingFees && !paypalFeeQuote ? <CircularProgress size={35} /> : null}
                      {(!payProcessingFees || paypalFeeQuote) && (
                        <>
                          <PayPalButtons
                            forceReRender={[
                              finalPaymentAmount,
                              paypalFeeQuote?.grossAmount,
                            ]}
                            fundingSource={"paypal"}
                            onClick={onPaypalClick}
                            createOrder={(data, actions) =>
                              createOrder(data, actions, "paypal-wallet")
                            }
                            onApprove={(data, actions) => onApprove(data, actions)}
                            onCancel={onPaypalCancel}
                            onError={onPaypalError}
                          />
                          <PayPalButtons
                            forceReRender={[
                              finalPaymentAmount,
                              paypalFeeQuote?.grossAmount,
                            ]}
                            fundingSource={"card"}
                            onClick={onPaypalClick}
                            createOrder={(data, actions) =>
                              createOrder(data, actions, "paypal-card")
                            }
                            onApprove={(data, actions) => onApprove(data, actions)}
                            onCancel={onPaypalCancel}
                            onError={onPaypalError}
                          />
                        </>
                      )}
                    </PayPalScriptProvider>
                  )}
                </>
              )}
              {paymentType === "bankTransfer" && (
                <>
                  {showBankTransferRegInstructions && (
                    <Typography style={{ marginTop: "1rem" }}>
                      Your Application for Team Registration will only be deemed
                      complete and ready for review after valid proof of bank
                      transfer is received by the Vis East Moot Administration.
                    </Typography>
                  )}                  
                  <Typography style={{ marginTop: "1rem" }}>
                    Please email your proof of bank transfer to{" "}
                    <a href="mailto:info@cisgmoot.org" className="color-blue">
                      info@cisgmoot.org
                    </a>
                    . Please ensure that your School is included in the
                    Reference/Message for easy traceability.
                  </Typography>
                  <Typography style={lineStyle}>
                    <span style={labelStyle}>Beneficiary Name:</span> Vis East
                    Moot Foundation Ltd.
                  </Typography>
                  <Typography style={lineStyle}>
                    <span style={labelStyle}>Bank:</span> HSBC, 1 Queen's Road
                    Central, Hong Kong
                  </Typography>
                  <Typography style={lineStyle}>
                    <span style={labelStyle}>Account Number:</span> 817 365901
                    001
                  </Typography>
                  <Typography style={lineStyle}>
                    <span style={labelStyle}>Swift Code:</span> HSBCHKHHHKH
                  </Typography>
                  <Typography style={lineStyle}>
                    <span style={labelStyle}>
                      For local Hong Kong transfers, Bank Code:
                    </span>{" "}
                    004
                  </Typography>
                  <Typography style={lineStyle}>
                    <span style={labelStyle}>Beneficiary Address:</span> Room
                    106, 1/Floor, Justice Place West Wing, 11 Ice House Street,
                    Central, Hong Kong
                  </Typography>
                  <Typography style={lineStyle}>
                    <span style={labelStyle}>Reference:</span> For{" "}
                    {existingTeamData.school}
                  </Typography>
                  <Typography
                    style={{ margin: "2.5rem 0", fontWeight: "bold" }}
                  >
                    Please note that payment must be made in either Hong Kong
                    Dollars or U.S. Dollars. Any and all bank charges must be
                    borne by the sender.
                  </Typography>
                </>
              )}
            </>
          </div>
        </>
      )}
    </>
  );
};

export default connect(
  (state, ownProps) => ({
    router: state.router,
    existingTeamData: state.registration.teamData,
    selectedTeam: state.team.selectedTeam,
    onPaymentTypeChanged: ownProps.onPaymentTypeChanged,
    onPaymentCompleted: ownProps.onPaymentCompleted,
    paymentOverride: ownProps.paymentOverride,
    showExsitingPaymentNotification: ownProps.showExsitingPaymentNotification,
    transactionType: ownProps.transactionType,
    minAmount: ownProps.minAmount,
    maxAmount: ownProps.maxAmount,
    initialAmount: ownProps.initialAmount,
    paymentHelperText: ownProps.paymentHelperText,
    paymentLabel: ownProps.paymentLabel,
    showBankTransferRegInstructions: ownProps.showBankTransferRegInstructions,
    resetPaymentMethodOnCompletion: ownProps.resetPaymentMethodOnCompletion,
    paymentInProcess: ownProps.paymentInProcess,
    onPaymentInProcess: ownProps.onPaymentInProcess,
    balanceOwed: ownProps.balanceOwed,
  }),
  {
    saveTeamOrder: saveTeamOrder,
    getSelectedTeam: getSelectedTeam,
    createStripeConfirmationQuote: createStripeConfirmationQuote,
    finalizeStripePayment: finalizeStripePayment,
    createPaymentFeeQuote: createPaymentFeeQuote,
  },
)(PaymentProcessing);
